import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AccountContactModel, AccountProfileForm, AccountUserModel } from '../types'
import { useTypedSelector } from 'src/State/RootReducer'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'

export const useSaveUserData = (
  userId?: number
): {
  saveUserData: (
    formValues: AccountProfileForm,
    initialFormValues: AccountProfileForm,
    setFormValues?: (formValues: AccountProfileForm) => void,
    onSubmitUserData?: () => void
  ) => void
} => {
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const userIdFromStore = useTypedSelector(state => state.auth.userId)
  const userIdToUse = userId ?? userIdFromStore

  const saveUserData = (
    formValues: AccountProfileForm,
    initialFormValues: AccountProfileForm,
    setFormValues?: (formValues: AccountProfileForm) => void,
    onSubmitUserData?: () => void
  ): void => {
    const newUserData: AccountUserModel = {
      Name: formValues.lastName,
      Vorname: formValues.firstName,
      mailNotification: formValues.eMailNotification,
    }

    /** in the backend, street => PlaceOr, Ort => Address */
    const newContactData: AccountContactModel = {
      Address: formValues.city,
      PLZ: formValues.zipCode,
      PlaceOR: formValues.address,
      Land: formValues.country,
      Telefon: formValues.phoneNumber,
    }

    const putContact = authFetch({
      method: 'PUT',
      path: `users/${userIdToUse}/location`,
      payload: newContactData,
    })

    const putUser = authFetch({
      method: 'PUT',
      path: `users/${userIdToUse}`,
      payload: newUserData,
    })

    const promises = [putContact, putUser]

    if (formValues.eMail !== initialFormValues.eMail) {
      const changeUserEmail = authFetch<void>({
        path: `users/${userIdToUse}/change-email`,
        method: 'PUT',
        payload: { email: formValues.eMail, app: process.env.REACT_APP_NAME },
      })
      promises.push(changeUserEmail)
    }

    Promise.all(promises)
      .then(() => {
        enqueueSuccessStack(t('user.save.success'))
        setFormValues?.({
          username: formValues.username,
          eMail: formValues.eMail,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          address: formValues.address,
          zipCode: formValues.zipCode,
          city: formValues.city,
          country: formValues.country,
          phoneNumber: formValues.phoneNumber,
          eMailNotification: formValues.eMailNotification,
        })
        onSubmitUserData?.()
      })
      .catch((error: AuthFetchError) => {
        if (error.response?.data.errno === FetchErrorCodes.UsernameAlreadyInUse) {
          enqueueErrorStack(t('user.save.error-username-already-in-use', { error: error.message }))
        } else {
          enqueueErrorStack(t('user.save.error', { error: error.message }))
        }
      })
  }
  return { saveUserData }
}
